/**
 * Payment platform detection + provider routing.
 *
 *   web         → Stripe                (if enabled by admin)
 *   ios-app     → Apple IAP             (StoreKit 2)
 *   android-app → Google Play Billing
 *
 * "ios-app" / "android-app" detection covers BOTH the Capacitor
 * native shell and the Natively WebView wrapper, because both expose
 * detectable globals (`window.Capacitor`, `window.natively`).
 */
import { useQuery } from '@tanstack/react-query';
import { getPaymentsConfig } from './api';

/** Returns one of 'ios-app' | 'android-app' | 'web'. */
export function getPaymentPlatform() {
  if (typeof window === 'undefined') return 'web';

  // 1) Capacitor — ONLY trust it when it reports a native platform.
  // The Capacitor JS shim is loaded even in regular web builds, where
  // `getPlatform()` returns 'web' and `isNativePlatform()` is false.
  try {
    const cap = window.Capacitor;
    if (cap && cap.isNativePlatform && cap.isNativePlatform()) {
      const p = cap.getPlatform && cap.getPlatform();
      if (p === 'ios') return 'ios-app';
      if (p === 'android') return 'android-app';
    }
  } catch {
    /* ignore */
  }

  // 2) Natively wrapper.
  // Some Natively builds do NOT expose `window.natively.platform`,
  // but they DO inject `Natively/iOS` or `Natively/Android` into the
  // user-agent string. Use either signal — the global is just a hint
  // that we're inside the wrapper, then UA tells us which OS.
  try {
    const n = window.natively;
    const ua = (navigator.userAgent || '').toLowerCase();
    const looksLikeNatively = !!n || ua.includes('natively/');
    if (looksLikeNatively) {
      // Prefer the explicit field when present.
      const explicit = (n?.platform || '').toLowerCase();
      if (explicit === 'ios') return 'ios-app';
      if (explicit === 'android') return 'android-app';
      // Fallback: derive from UA.
      if (ua.includes('natively/ios') || /iphone|ipad|ipod/.test(ua)) {
        return 'ios-app';
      }
      if (ua.includes('natively/android') || /android/.test(ua)) {
        return 'android-app';
      }
    }
  } catch {
    /* ignore */
  }

  return 'web';
}

/**
 * Pick which payment provider to surface in the UI based on platform
 * + admin toggles. Returns 'apple' | 'google' | 'stripe' | 'none'.
 * `none` means: no provider available (e.g. iOS user but Apple IAP
 * toggle off in admin).
 */
export function pickProvider(platform, providers) {
  if (!providers) return 'none';
  if (platform === 'ios-app') {
    if (providers.apple_iap) return 'apple';
    if (providers.stripe) return 'stripe'; // fallback (not usually allowed)
    return 'none';
  }
  if (platform === 'android-app') {
    if (providers.google_play) return 'google';
    if (providers.stripe) return 'stripe';
    return 'none';
  }
  // Web
  if (providers.stripe) return 'stripe';
  return 'none';
}

/**
 * React Query hook — public payment config. Cached aggressively (5
 * minutes) since the admin rarely changes these settings.
 */
export function usePaymentsConfig() {
  return useQuery({
    queryKey: ['payments', 'config'],
    queryFn: async () => {
      try {
        const res = await getPaymentsConfig();
        return res.data;
      } catch {
        // Failing closed (no providers) is safer than failing open.
        return {
          providers: { stripe: false, apple_iap: false, google_play: false },
          ios: { configured: false },
          android: { configured: false },
        };
      }
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}
